import { trpc } from '../../lib/trpc';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Group, Space, TextInput } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { BadgeCheck } from 'lucide-react';
import { use } from 'react';
import { useForm } from 'react-hook-form';
import { ulid } from 'ulid';
import { z } from 'zod';

const RenameForm = ({
  id,
  modalId,
}: {
  readonly id: string;
  readonly modalId: string;
}) => {
  const utils = trpc.useUtils();
  const modelData = use(
    trpc.models.show.useQuery(id, { experimental_prefetchInRender: true })
      .promise,
  );
  const { formState, handleSubmit, register } = useForm({
    defaultValues: { name: '' },
    resolver: zodResolver(
      z.object({ name: z.string().trim().min(1, 'Name is required') }),
    ),
  });
  const { mutateAsync: updateModelAsync } = trpc.models.update.useMutation();
  const submitHandler = handleSubmit(async (data) => {
    await updateModelAsync({ data, id });
    notifications.show({
      message: (
        <>
          <BadgeCheck stroke="var(--color-lime-400)" /> Changes saved
        </>
      ),
    });
    utils.models.list.invalidate();
    utils.models.show.invalidate();
    modals.close(modalId);
  });
  return (
    <form onSubmit={submitHandler}>
      <TextInput
        error={formState.errors.name?.message?.toString()}
        {...register('name')}
        placeholder={modelData?.name}
      />
      <Space h="lg" />
      <Group justify="end">
        <Button
          onClick={() => modals.close(modalId)}
          variant="outline"
        >
          Cancel
        </Button>
        <Button type="submit">Save</Button>
      </Group>
    </form>
  );
};

export const useRename = ({ id }: { id: string }) => {
  const modalId = ulid();

  const initiateRename = () =>
    modals.open({
      children: (
        <RenameForm
          id={id}
          modalId={modalId}
        />
      ),
      modalId,
      title: <strong>Rename content type</strong>,
    });
  return { initiateRename };
};
